import Modal from "components/DesignSystem/Modal/Modal";
import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { INCOME_STATEMENT } from "constants/bookkeeping";
import {
  FinancialStatementReportItem,
  ReportItem,
} from "types/Models/consolidatedReport";
import { VIEW_BY_OPTIONS } from "constants/reports";
import { PERIOD } from "constants/revenueMetrics";
import { useGetAllClassificationQuery } from "store/apis/classifications";
import { FormikForm } from "components/FormikForm/FormikForm";
import { getUsedGroupBysForReport } from "utils/allReports";

type EditFinancialStatementModalProps = {
  isOpen: boolean;
  onClose: () => void;
  report: FinancialStatementReportItem | null;
  onSave: (data: {
    groupBy: string;
    groupByLabel: string;
    classIds?: string[];
  }) => void;
  modalTitle: string;
  submitText: string;
  isEditMode: boolean;
  entityId: string;
  selectedReports?: ReportItem[];
};

type FormValues = {
  selectedValue: string;
  selectedLabel: string;
};

export const FinancialStatementModal = ({
  isOpen,
  onClose,
  report,
  onSave,
  modalTitle,
  submitText,
  isEditMode,
  entityId,
  selectedReports = [],
}: EditFinancialStatementModalProps) => {
  const { data: classification = [] } = useGetAllClassificationQuery(
    { entityId: entityId },
    { skip: !entityId || report?.value !== INCOME_STATEMENT }
  );

  const usedGroupBys = report
    ? getUsedGroupBysForReport(report.value, selectedReports)
    : [];

  const toOption = (label: string, value: string): OptionData => ({
    label,
    value,
    disableOption: usedGroupBys.includes(value),
    data: usedGroupBys.includes(value) ? "Added already" : undefined,
  });

  const noneOption = toOption("None", "");

  const classificationGroup =
    report?.value === INCOME_STATEMENT && classification.length !== 0
      ? [
          {
            label: "Classification Groups",
            options: classification.map(({ name, uuid }) =>
              toOption(name, uuid)
            ),
          },
        ]
      : [];

  const periodGroup = [
    {
      label: "Period",
      options: VIEW_BY_OPTIONS.filter(
        ({ value }) => value !== PERIOD.TOTALS
      ).map(({ label, value }) => toOption(label, value)),
    },
  ];

  const groupByOptions = [noneOption, ...classificationGroup, ...periodGroup];

  const allFlatOptions = [
    noneOption,
    ...classificationGroup.flatMap((g) => g.options),
    ...periodGroup.flatMap((g) => g.options),
  ];

  const classificationIds = classification.map((c) => c.uuid);

  const periodValues = VIEW_BY_OPTIONS.filter(
    ({ value }) => value !== PERIOD.TOTALS
  ).map(({ value }) => value);

  const allPossibleValues = [
    ...(report?.value === INCOME_STATEMENT ? classificationIds : []),
    ...periodValues,
  ];

  const nextAvailableValue =
    allPossibleValues.find((value) => !usedGroupBys.includes(value)) || "";

  let initialSelectedValue;
  if (!report) initialSelectedValue = nextAvailableValue;
  if (report?.classIds?.length && report?.value === INCOME_STATEMENT)
    initialSelectedValue = report?.classIds[0];

  if (isEditMode && report?.groupBy === "") {
    initialSelectedValue = "";
  } else {
    initialSelectedValue = report?.groupBy || nextAvailableValue;
  }

  const initialSelectedLabel =
    (allFlatOptions.find((opt) => opt.value === initialSelectedValue)
      ?.label as string) ||
    report?.groupByLabel ||
    "";

  const initialValues: FormValues = {
    selectedValue: initialSelectedValue,
    selectedLabel: initialSelectedLabel,
  };

  const handleSubmit = (values: FormValues) => {
    const isPeriodGrouping = VIEW_BY_OPTIONS.some(
      (option) => option.value === values.selectedValue
    );

    const saveData = {
      groupBy: isPeriodGrouping ? values.selectedValue : "",
      groupByLabel: values.selectedLabel,
      classIds:
        !isPeriodGrouping && values.selectedValue ? [values.selectedValue] : [],
    };

    onSave(saveData);
    onClose();
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={onClose}>
      <Modal.Content size="regular" useCustomOverlay asChild>
        <FormikForm initialValues={initialValues} onSubmit={handleSubmit}>
          {({ values, setFieldValue }) => {
            const selectedOption = allFlatOptions.find(
              (opt) => opt.value === values.selectedValue
            ) ?? { label: "None", value: "" };

            return (
              <>
                <Modal.Header>
                  <Modal.Title>{modalTitle}</Modal.Title>
                  <Modal.Close />
                </Modal.Header>

                <Modal.Body>
                  <div className="t-flex t-flex-col t-gap-4">
                    <div>
                      <Combobox
                        name="selectedValue"
                        label="Group by"
                        placeholder="None"
                        options={groupByOptions}
                        value={selectedOption}
                        onChange={(selected) => {
                          if (selected && "value" in selected) {
                            setFieldValue("selectedValue", selected.value);
                            setFieldValue(
                              "selectedLabel",
                              String(selected.label || "")
                            );
                          }
                        }}
                        block
                        required
                        withForm
                        menuPortalTarget={document.body}
                        isClearable={false}
                        tooltip
                        isOptionDisabled={(option) =>
                          Boolean(option.disableOption)
                        }
                      />
                    </div>
                  </div>
                </Modal.Body>

                <Modal.FooterButtonGroup>
                  <Button customType="secondary" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button customType="primary" type="submit">
                    {submitText}
                  </Button>
                </Modal.FooterButtonGroup>
              </>
            );
          }}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
